import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { QRCodeSVG } from "qrcode.react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ScanLine, Wallet, GraduationCap, MapPin } from "lucide-react";
import StaffCheckInCard from "@/components/app/StaffCheckInCard";
import AvatarUpload from "@/components/app/PhotoUpload";
import { PageHeader } from "@/components/app/PageHeader";
import { Link } from "react-router-dom";

// ---------- Parent Dashboard ----------
export function ParentHome() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState({});
  const [kids, setKids] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/students").then((r) => setKids(r.data.students)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={`${t("hello")}, ${user?.name.split(" ")[0]}`}
        subtitle={t("parent_dashboard_title")}
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <Card className="pmm-pop group rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.05s" }}><CardContent className="flex items-center gap-4 p-6">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-orange-600 to-orange-400 text-white shadow-lg shadow-orange-500/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"><GraduationCap className="h-6 w-6" /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("myChildren")}</div>
            <div className="num font-display text-3xl font-extrabold tracking-tight text-blue-950">{summary.my_children ?? 0}</div>
          </div>
        </CardContent></Card>
        <Card className="pmm-pop group rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.12s" }}><CardContent className="flex items-center gap-4 p-6">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-blue-900 to-blue-600 text-white shadow-lg shadow-blue-800/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"><Wallet className="h-6 w-6" /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Paid fees</div>
            <div className="num font-display text-3xl font-extrabold tracking-tight text-blue-950">{summary.paid_payments ?? 0}</div>
          </div>
        </CardContent></Card>
        <Card className="pmm-pop group rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.19s" }}><CardContent className="flex items-center gap-4 p-6">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 text-white shadow-lg shadow-yellow-500/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"><Wallet className="h-6 w-6" /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Pending</div>
            <div className="num font-display text-3xl font-extrabold tracking-tight text-blue-950">{summary.pending_payments ?? 0}</div>
          </div>
        </CardContent></Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.26s" }}><CardContent className="p-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">{t("myChildren")}</h2>
            <Link to="/parent/children"><Button variant="ghost" size="sm" className="rounded-full font-bold text-orange-600 hover:bg-orange-50 hover:text-orange-700">→</Button></Link>
          </div>
          {kids.length === 0 ? (
            <p className="text-sm text-muted-foreground">No children yet. <Link className="font-bold text-orange-600 underline" to="/parent/children">{t("registerChild")}</Link></p>
          ) : (
            <ul className="space-y-2">
              {kids.map((k) => (
                <li key={k.id} className="flex items-center justify-between rounded-xl border-l-4 border-orange-500 bg-orange-50/50 px-4 py-3 transition-all duration-300 hover:translate-x-1 hover:shadow-md">
                  <div>
                    <div className="font-display font-extrabold text-blue-950">{k.full_name}</div>
                    <div className="num text-xs font-semibold text-muted-foreground">{k.level}{k.school ? ` · ${k.school}` : ""}</div>
                  </div>
                  <GraduationCap className="h-5 w-5 text-orange-500" />
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>

        <Card className="pmm-cinematic relative overflow-hidden rounded-[1.5rem] border-0 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 shadow-2xl shadow-blue-900/25"><CardContent className="relative p-6">
          <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-orange-500/25 blur-3xl" />
          <h2 className="font-display text-sm font-extrabold uppercase tracking-widest text-yellow-400">{t("scanQR")}</h2>
          <p className="mt-2 text-sm font-semibold text-blue-200">{t("parentScanInstruction")}</p>
          <Link to="/parent/checkin">
            <Button className="mt-5 h-11 gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105" data-testid="go-checkin">
              <ScanLine className="h-4 w-4" /> {t("scanQR")}
            </Button>
          </Link>
        </CardContent></Card>
      </div>
    </div>
  );
}

// ---------- Parent Children ----------
export function ParentChildrenPage() {
  const { t } = useI18n();
  const [kids, setKids] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", level: "", school: "", date_of_birth: "", notes: "" });
  const load = () => api.get("/students").then((r) => setKids(r.data.students));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/students", form);
      toast.success("Child added");
      setOpen(false);
      setForm({ full_name: "", level: "", school: "", date_of_birth: "", notes: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const uploadKidPhoto = async (studentId, dataUrl) => {
    try {
      await api.put(`/students/${studentId}/photo`, { photo_data_url: dataUrl });
      toast.success("Gambar disimpan");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="parent-children">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={t("myChildren")}
        subtitle="Daftarkan anak anda untuk Kelas Program Membaca PMM."
        action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105" data-testid="add-child-btn"><Plus className="h-4 w-4" /> {t("registerChild")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("registerChild")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("fullName")}</Label><Input data-testid="child-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("level")}</Label><Input data-testid="child-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="h-11" placeholder="Tahap 1 (Asas)" /></div>
                <div><Label>{t("dateOfBirth")}</Label><Input data-testid="child-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="h-11" /></div>
              </div>
              <div><Label>{t("school")}</Label><Input data-testid="child-school" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="h-11" /></div>
              <Button onClick={save} data-testid="child-save" className="h-11 w-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display font-extrabold uppercase tracking-wide text-white">{t("saveChild")}</Button>
            </div>
          </DialogContent>
        </Dialog>
        }
      />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {kids.map((k, i) => (
          <Card key={k.id} className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`child-card-${k.id}`} style={{ animationDelay: `${0.08 + i * 0.06}s` }}>
            <CardContent className="p-6">
              <div className="mb-3">
                <AvatarUpload
                  name={k.full_name}
                  photo={k.photo_data_url}
                  onUpload={(dataUrl) => uploadKidPhoto(k.id, dataUrl)}
                  size="lg"
                  testId={`child-avatar-${k.id}`}
                />
              </div>
              <div className="font-display text-lg font-extrabold text-blue-950">{k.full_name}</div>
              <span className="num mt-1 inline-block rounded-full bg-blue-800/10 px-2.5 py-0.5 text-xs font-bold text-blue-800">{k.level}</span>
              {k.school && <div className="mt-1 text-xs font-semibold text-muted-foreground">{k.school}</div>}
            </CardContent>
          </Card>
        ))}
        {kids.length === 0 && <p className="text-sm text-muted-foreground">{t("noRecordsYet")}</p>}
      </div>
    </div>
  );
}

// ---------- Parent Check-In (scan wall QR flow) ----------
export function ParentCheckInPage() {
  const { t, lang } = useI18n();
  const [kids, setKids] = useState([]);
  const [config, setConfig] = useState({ center_code: "MAIN-OFFICE", wall_qr_payload: "TUITION://MAIN-OFFICE" });
  const [busyId, setBusyId] = useState(null);
  const [selectedKid, setSelectedKid] = useState("");
  const [action, setAction] = useState("check_in");
  const [scanning, setScanning] = useState(false);
  const scannerRef = React.useRef(null);

  useEffect(() => {
    api.get("/students").then((r) => {
      setKids(r.data.students);
      if (r.data.students.length > 0) setSelectedKid((prev) => prev || r.data.students[0].id);
    }).catch(() => {});
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (e) { /* already stopped */ }
      try { scannerRef.current.clear(); } catch (e) { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const submitAttendance = async (student_id, act) => {
    setBusyId(student_id + act);
    let geo = null;
    try {
      const p = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000 }),
      );
      geo = { latitude: p.coords.latitude, longitude: p.coords.longitude };
    } catch (e) {
      // geolocation is optional for QR check-in
    }
    try {
      await api.post("/attendance/student/qr", {
        center_code: config.center_code,
        student_id,
        action: act,
        latitude: geo?.latitude,
        longitude: geo?.longitude,
      });
      const kid = kids.find((k) => k.id === student_id);
      toast.success(
        `${kid ? kid.full_name + " — " : ""}${act === "check_in" ? t("checkIn") : t("checkOut")} ✓`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const startScanner = async () => {
    if (!selectedKid) {
      toast.error(lang === "ms" ? "Sila pilih anak dahulu" : "Please select a child first");
      return;
    }
    setScanning(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("qr-scan-region");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (decodedText) => {
          const expected = config.wall_qr_payload || `TUITION://${config.center_code}`;
          if (decodedText === expected || decodedText.includes(config.center_code)) {
            await stopScanner();
            await submitAttendance(selectedKid, action);
          } else {
            toast.error(lang === "ms" ? "Kod QR tidak dikenali" : "QR code not recognised");
          }
        },
        () => { /* scan errors are frequent while aiming; ignore */ },
      );
    } catch (err) {
      await stopScanner();
      toast.error(
        lang === "ms"
          ? "Kamera tidak tersedia. Gunakan sahkan manual di bawah."
          : "Camera unavailable. Use manual confirm below.",
      );
    }
  };

  return (
    <div className="space-y-6" data-testid="parent-checkin">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={t("scanQR")}
        subtitle={t("parentScanInstruction")}
      />
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md" style={{ animationDelay: "0.08s" }}><CardContent className="p-6">
          <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-orange-600 to-orange-400 text-white shadow-md shadow-orange-500/30"><ScanLine className="h-4 w-4" /></span>
            {lang === "ms" ? "Imbas dengan kamera" : "Scan with camera"}
          </h2>
          <div className="mt-4 space-y-3">
            <div>
              <Label>{lang === "ms" ? "Pilih anak" : "Select child"}</Label>
              <Select value={selectedKid} onValueChange={setSelectedKid}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="scan-child-select">
                  <SelectValue placeholder={lang === "ms" ? "Pilih anak" : "Select child"} />
                </SelectTrigger>
                <SelectContent>
                  {kids.map((k) => (
                    <SelectItem key={k.id} value={k.id}>{k.full_name} — {k.level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={action === "check_in" ? "default" : "outline"}
                onClick={() => setAction("check_in")}
                className="rounded-full h-10"
                data-testid="scan-action-in"
              >
                {t("checkIn")}
              </Button>
              <Button
                variant={action === "check_out" ? "default" : "outline"}
                onClick={() => setAction("check_out")}
                className="rounded-full h-10"
                data-testid="scan-action-out"
              >
                {t("checkOut")}
              </Button>
            </div>
            <div id="qr-scan-region" className={`overflow-hidden rounded-2xl ${scanning ? "min-h-64 bg-black" : ""}`} />
            {!scanning ? (
              <Button onClick={startScanner} className="h-11 w-full gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105" data-testid="start-scan-btn">
                <ScanLine className="h-4 w-4" /> {lang === "ms" ? "Mula imbas QR" : "Start QR scan"}
              </Button>
            ) : (
              <Button onClick={stopScanner} variant="secondary" className="w-full h-11 rounded-full" data-testid="stop-scan-btn">
                {lang === "ms" ? "Henti imbas" : "Stop scanning"}
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              {lang === "ms"
                ? "Halakan kamera ke poster QR di pejabat untuk merekod kehadiran."
                : "Point the camera at the office QR poster to record attendance."}
            </p>
          </div>
        </CardContent></Card>

        <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md" style={{ animationDelay: "0.14s" }}><CardContent className="p-6">
          <h2 className="font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">
            {lang === "ms" ? "Atau sahkan secara manual" : "Or confirm manually"}
          </h2>
          <div className="mt-4 space-y-3">
            {kids.map((k) => (
              <div key={k.id} className="rounded-xl border-l-4 border-blue-700 bg-blue-50/50 p-4 transition-all duration-300 hover:translate-x-1 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display font-extrabold text-blue-950">{k.full_name}</div>
                    <div className="num text-xs font-semibold text-muted-foreground">{k.level}</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button size="sm" onClick={() => submitAttendance(k.id, "check_in")} disabled={busyId === k.id + "check_in"} data-testid={`checkin-${k.id}`} className="rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-bold text-white shadow-md shadow-orange-500/30 transition-transform duration-300 hover:scale-105">
                    <MapPin className="mr-1 h-3.5 w-3.5" /> {t("checkIn")}
                  </Button>
                  <Button size="sm" onClick={() => submitAttendance(k.id, "check_out")} disabled={busyId === k.id + "check_out"} data-testid={`checkout-${k.id}`} className="rounded-full bg-gradient-to-r from-blue-900 to-blue-700 font-bold text-white shadow-md shadow-blue-800/30 transition-transform duration-300 hover:scale-105">
                    {t("checkOut")}
                  </Button>
                </div>
              </div>
            ))}
            {kids.length === 0 && <p className="text-sm text-muted-foreground">No children yet.</p>}
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

// ---------- Parent Payments ----------
export function ParentPaymentsPage() {
  const { t } = useI18n();
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [kids, setKids] = useState([]);
  const [busy, setBusy] = useState(null);
  const [tpConfig, setTpConfig] = useState({ configured: false });
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data.packages));
    api.get("/payments/history").then((r) => setHistory(r.data.transactions));
    api.get("/students").then((r) => setKids(r.data.students));
    api.get("/payments/toyyibpay/config").then((r) => setTpConfig(r.data)).catch(() => {});
  }, []);

  const buyStripe = async (pkg, student_id) => {
    setBusy(pkg.id + "_stripe");
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        student_id: student_id || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const buyToyyibpay = async (pkg, student_id) => {
    if (!tpConfig.configured) {
      toast.error("ToyyibPay is not configured yet — ask admin to add credentials.");
      return;
    }
    if (!phone) {
      toast.error("Please enter your phone number for the FPX bill.");
      return;
    }
    setBusy(pkg.id + "_toyyib");
    try {
      const { data } = await api.post("/payments/toyyibpay/checkout", {
        package_id: pkg.id,
        student_id: student_id || null,
        origin_url: window.location.origin,
        phone,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="parent-payments">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={t("payments")}
        subtitle="Pay tuition fees securely — card via Stripe or FPX via ToyyibPay."
      />

      <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md" style={{ animationDelay: "0.08s" }}>
        <CardContent className="p-6">
          <Label className="text-sm">Phone number (for FPX bill)</Label>
          <Input
            data-testid="tp-phone"
            className="mt-2 h-11 max-w-sm"
            placeholder="+60123456789"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {!tpConfig.configured && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="tp-not-configured">
              ToyyibPay is running in {tpConfig.environment || "dev"} mode and needs credentials — Stripe is available now.
            </p>
          )}
        </CardContent>
      </Card>

      <section>
        <h2 className="mb-3 font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">{t("feePackages")}</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((p, i) => (
            <Card key={p.id} className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`pkg-${p.id}`} style={{ animationDelay: `${0.1 + i * 0.06}s` }}>
              <CardContent className="p-6">
                <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-blue-900 to-blue-600 text-white shadow-lg shadow-blue-800/30">
                  <Wallet className="h-6 w-6" />
                </div>
                <div className="font-display text-lg font-extrabold text-blue-950">{p.name}</div>
                <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
                <div className="num mt-4 font-display text-3xl font-extrabold tracking-tight text-blue-950">
                  {p.currency.toUpperCase()} {Number(p.amount).toFixed(2)}
                </div>

                <div className="mt-4 space-y-3">
                  {(kids.length > 0 ? kids : [null]).map((k) => (
                    <div key={k ? k.id : "self"} className="rounded-xl border border-border/60 p-3">
                      {k && <div className="mb-2 text-xs font-medium text-muted-foreground">{t("payFor")} <span className="text-foreground">{k.full_name}</span></div>}
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          onClick={() => buyStripe(p, k ? k.id : null)}
                          disabled={busy === p.id + "_stripe"}
                          className="h-10 gap-1 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-bold text-white shadow-md shadow-orange-500/30 transition-transform duration-300 hover:scale-105"
                          data-testid={`buy-stripe-${p.id}${k ? "-" + k.id : ""}`}
                        >
                          Card
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => buyToyyibpay(p, k ? k.id : null)}
                          disabled={busy === p.id + "_toyyib" || !tpConfig.configured}
                          className="h-10 gap-1 rounded-full bg-gradient-to-r from-blue-900 to-blue-700 font-bold text-white shadow-md shadow-blue-800/30 transition-transform duration-300 hover:scale-105"
                          data-testid={`buy-toyyib-${p.id}${k ? "-" + k.id : ""}`}
                          title={!tpConfig.configured ? "ToyyibPay not configured" : ""}
                        >
                          FPX
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">{t("history")}</h2>
        <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.2s" }}><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-blue-950 hover:bg-blue-950">
              <TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Package</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Amount</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">Status</TableHead><TableHead className="text-xs font-extrabold uppercase tracking-widest text-yellow-400">When</TableHead>
            </TableRow></TableHeader>
            <TableBody>{history.map((r) => (
              <TableRow key={r.session_id} className="transition-colors hover:bg-orange-50">
                <TableCell className="font-semibold text-blue-950">{r.package_name}</TableCell>
                <TableCell className="num font-semibold">{(r.currency || "").toUpperCase()} {Number(r.amount).toFixed(2)}</TableCell>
                <TableCell><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${r.payment_status === "paid" ? "bg-green-500/10 text-green-600" : "bg-yellow-500/15 text-yellow-600"}`}>{r.payment_status}</span></TableCell>
                <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}{history.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
          </Table>
        </CardContent></Card>
      </section>
    </div>
  );
}
